import ActiveUserDetailsForm from "@/components/profile/ActiveUserDetailsForm";
import EmptyState from "@/components/EmptyState";
import PhotoUploadGrid from "@/components/onboarding/PhotoUploadGrid";
import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useGetUserProfile } from "@/tanstack-hooks";
import { CheckCircle2Icon, CloudOffIcon, EyeIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import IntroConfigCard from "@/components/profile/IntroConfigCard";
import { useTranslation } from "@/hooks/useTranslation";
import { useStandaloneMode } from "@/hooks/useStandaloneMode";

export default function ProfileEditPage() {
  const navigate = useNavigate();
  const location = useLocation(); // Add this line
  const userProfileQuery = useGetUserProfile();
  const isStandalone = useStandaloneMode();
  const { toast } = useToast();
  const t = useTranslation();

  const handleSuccessfulUpdate = () => {
    toast({
      variant: "default",
      // @ts-expect-error Type 'Element' is not assignable to type
      title: (
        <div className="flex items-center gap-2">
          <CheckCircle2Icon className="h-4 w-4" />
          <span>{t.SELF_PROFILE_VIEW.LABELS.EDIT_SUCCESS}</span>
        </div>
      ),
      className: "dark rounded-2xl",
    });
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (location.state?.scrollToBottom) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [location]);

  if (userProfileQuery.isLoading) {
    return <Spinner />;
  }

  if (userProfileQuery.error || !userProfileQuery.data) {
    return (
      <EmptyState
        icon={CloudOffIcon}
        title={t.SELF_PROFILE_VIEW.ERRORS.LOADING}
        message={t.SELF_PROFILE_VIEW.ERRORS.LOADING_DESCRIPTION}
      />
    );
  }

  const userProfile = userProfileQuery.data;

  return (
    <div
      className={`flex flex-col w-full max-w-5xl mx-auto mt-4 ${
        isStandalone ? "pb-24" : "pb-16"
      }`}
    >
      <div className="flex flex-row justify-between items-center mb-4">
        <span className="text-xl ml-1 font-semibold">
          {t.SELF_PROFILE_VIEW.LABELS.EDIT}
        </span>
        <Button
          variant="secondary"
          className="primary"
          onClick={() => navigate("/profile")}
        >
          <EyeIcon className="mr-2 h-4 w-4" />
          {t.SELF_PROFILE_VIEW.LABELS.VIEW_BUTTON}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-4">
          <div className="h-fit">
            <PhotoUploadGrid />
          </div>
          <IntroConfigCard currentIntro={userProfile.intro} />
        </div>
        <div className="h-fit">
          <ActiveUserDetailsForm
            user={userProfile}
            onSaveSuccess={handleSuccessfulUpdate}
          />
        </div>
      </div>
    </div>
  );
}
